import { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
    getRecord,
    getCompanies,
    getXeroConfigsForCompany,
    getCustomers,
    getAccountCodes,
    setSelectedCompany,
    setSelectedConfig,
} from '../store/dataSlice'
import { getTaxTypesForDropdown } from 'services/XeroService'
import { selectCurrentCompany } from 'store/filterdata/filterDataSlice'

/**
 * Custom hook for managing invoice data loading and company selection
 * @param {string} id - Invoice ID (for edit mode)
 */
export const useInvoiceData = id => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [taxTypes, setTaxTypes] = useState([])
    const [previousCompanyId, setPreviousCompanyId] = useState(undefined)

    // Get global company filter from filterData state
    const globalCompany = useSelector(selectCurrentCompany)

    // Get user information from auth state
    const user = useSelector(state => state.auth.user)

    // Determine effective company: use globalCompany if available, otherwise use user's company for company admins
    // Use useMemo to prevent object recreation on every render
    const effectiveCompany = useMemo(() => {
        if (globalCompany) return globalCompany
        if (user && !user.super_user && user.company_id) {
            return { id: user.company_id }
        }
        return null
    }, [globalCompany, user])

    // Get data from xero store
    const { recordData, loading, companies, selectedCompany, xeroConfigs, xeroConfig, customers, accountCodes } = useSelector(
        state =>
            state.xero?.data || {
                recordData: null,
                loading: false,
                companies: [],
                selectedCompany: null,
                xeroConfigs: [],
                xeroConfig: null,
                customers: [],
                accountCodes: [],
            }
    )

    // Detect company changes and redirect to index page
    useEffect(() => {
        const currentCompanyId = globalCompany?.id
        // Skip on initial render
        if (previousCompanyId !== undefined && currentCompanyId !== previousCompanyId) {
            console.log('Company changed in navbar from', previousCompanyId, 'to', currentCompanyId)
            // Show alert
            alert('Company changed. Redirecting to invoice list.')
            // Redirect to index page
            navigate('/finacials/xero')
        } else {
            // Update previous company ID
            setPreviousCompanyId(currentCompanyId)
        }
    }, [globalCompany, navigate, previousCompanyId])

    // Load initial data once on component mount
    useEffect(() => {
        console.log('Component mounted, loading initial data')
        dispatch(getCompanies())
        setTaxTypes(getTaxTypesForDropdown())

        // If we're in edit mode, load the invoice
        if (id) {
            console.log('Edit mode detected, loading invoice with ID:', id)
            dispatch(getRecord(id))
        }
    }, [dispatch, id])

    // Handle effective company - only run when companies are loaded or effectiveCompany changes
    useEffect(() => {
        // If effective company is available, use it
        if (effectiveCompany && companies && companies.length > 0) {
            console.log('Using effective company:', effectiveCompany)
            const matchingCompany = companies.find(c => c.id === effectiveCompany.id)
            if (matchingCompany) {
                // Only dispatch if company is different to prevent unnecessary re-renders
                if (!selectedCompany || selectedCompany.id !== matchingCompany.id) {
                    console.log('Setting selected company')
                    dispatch(setSelectedCompany(matchingCompany))
                    dispatch(getXeroConfigsForCompany(matchingCompany.id))
                    // Load customers and account codes only if we don't have them yet
                    if (!customers.length || !accountCodes.length) {
                        console.log('Loading initial customers and account codes for company:', matchingCompany.id)
                        dispatch(getCustomers({ companyId: matchingCompany.id }))
                        dispatch(getAccountCodes({ companyId: matchingCompany.id }))
                    }
                }
            }
        }
    }, [dispatch, effectiveCompany, companies, selectedCompany, customers.length, accountCodes.length])

    // Debug recordData when it changes in edit mode
    useEffect(() => {
        if (id && recordData) {
            console.log('=== DEBUGGING EDIT MODE DATA ===')
            console.log('Record data loaded:', recordData)
            console.log('Contact:', recordData.contact)
            console.log('Line items:', recordData.line_items)
            console.log('Company ID:', recordData.company_id)
            console.log('Configuration ID:', recordData.configuration_id)
            console.log('=== END DEBUGGING EDIT MODE DATA ===')

            // Load Xero configurations and data for edit mode
            if (recordData.configuration_id) {
                console.log('Loading Xero configurations for configuration ID:', recordData.configuration_id)
                dispatch(getXeroConfigsForCompany({ configId: recordData.configuration_id }))

                // Also load customers and account codes for this specific configuration
                // This ensures we have the right data for the invoice being edited
                console.log('Loading customers and account codes for edit mode with config:', recordData.configuration_id)
                dispatch(getCustomers({ configurationId: recordData.configuration_id }))
                dispatch(getAccountCodes({ configurationId: recordData.configuration_id }))
            } else if (recordData.company_id) {
                // Fallback to company-level data if no configuration ID
                console.log('Loading customers and account codes for edit mode with company:', recordData.company_id)
                dispatch(getCustomers({ companyId: recordData.company_id }))
                dispatch(getAccountCodes({ companyId: recordData.company_id }))
            }
        }
    }, [id, recordData, dispatch])

    // Debug customers and account codes when they change
    useEffect(() => {
        console.log('Customers updated:', customers)
        console.log('Customer count:', customers.length)
    }, [customers])

    useEffect(() => {
        console.log('Account codes updated:', accountCodes)
        console.log('Account code count:', accountCodes.length)
    }, [accountCodes])

    // Load customers and account codes when a configuration is selected
    // Only load if we don't already have data to prevent unnecessary calls
    useEffect(() => {
        if (xeroConfig && (!customers.length || !accountCodes.length)) {
            console.log('Loading customers and account codes for config:', xeroConfig.id)
            dispatch(getCustomers({ configurationId: xeroConfig.id }))
            dispatch(getAccountCodes({ configurationId: xeroConfig.id }))
        } else if (selectedCompany && !xeroConfig && (!customers.length || !accountCodes.length)) {
            console.log('Loading customers and account codes for company:', selectedCompany.id)
            // Fallback to company-level filtering if no specific config
            dispatch(getCustomers({ companyId: selectedCompany.id }))
            dispatch(getAccountCodes({ companyId: selectedCompany.id }))
        }
    }, [dispatch, selectedCompany, xeroConfig, customers.length, accountCodes.length])

    // Debug company and config data
    useEffect(() => {
        console.log('=== DEBUGGING COMPANY DATA ===')
        console.log('Companies loaded:', companies)
        console.log('Companies length:', companies?.length)
        console.log('Companies array:', JSON.stringify(companies, null, 2))
        if (companies && companies.length > 0) {
            console.log('First company object:', companies[0])
            console.log('First company keys:', Object.keys(companies[0]))
        }
        console.log('Selected Company:', selectedCompany)
        console.log('Xero Configs for company:', xeroConfigs)
        console.log('Selected Config:', xeroConfig)
        console.log('Loading state:', loading)
        if (xeroConfig) {
            console.log('Config ID:', xeroConfig.id)
            console.log('Tenant ID:', xeroConfig.tenant_id)
            console.log('Token expired:', xeroConfig.is_token_expired)
            console.log('Token expires at:', xeroConfig.token_expires_at)
            console.log('Is authorized:', !!(xeroConfig.tenant_id && !xeroConfig.is_token_expired))
            console.log('Company:', xeroConfig.company)
            console.log('Note: access_token is not included in API response (write-only field)')
        }
        console.log('=== END DEBUGGING ===')
    }, [companies, selectedCompany, xeroConfigs, xeroConfig, loading])

    const handleConfigChange = configId => {
        const selectedConfig = xeroConfigs.find(config => config.id === parseInt(configId))
        dispatch(setSelectedConfig(selectedConfig))
    }

    return {
        // State
        recordData,
        loading,
        companies,
        selectedCompany,
        xeroConfigs,
        xeroConfig,
        customers,
        accountCodes,
        taxTypes,
        globalCompany,
        // Handlers
        handleConfigChange,
        // Dispatch for external use
        dispatch,
    }
}
